import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './analytics.service';

/**
 * Owns the on/off decisions for create form v2 (docs/CREATE_EVENT_VOICE_PLAN.md),
 * so no component reads `environment` or PostHog for them itself.
 *
 * Same two-switch shape as GuestPhotoWallService:
 *   1. `environment.features.*` — build-time. Off means the feature does not exist.
 *   2. A PostHog kill-switch flag — runtime, no redeploy. It can only turn a
 *      feature OFF, so a blocked or slow PostHog leaves (1) in charge.
 */
@Injectable({ providedIn: 'root' })
export class CreateEventFeaturesService {
  static readonly ROWS_KILL_SWITCH = 'create-event-rows-off';

  constructor(private analytics: AnalyticsService) {}

  /** The grouped-rows layout with bottom sheets. Off = today's chip form. */
  rowsEnabled(): boolean {
    if (!environment.features.createEventRows) return false;
    return !this.analytics.isFlagOn(CreateEventFeaturesService.ROWS_KILL_SWITCH);
  }

  /** "Say it instead". Only exists on top of the rows layout. */
  voiceEnabled(): boolean {
    if (!this.rowsEnabled()) return false;
    if (!environment.features.voiceCreate) return false;
    const config = environment.voiceCreate;
    return !this.analytics.isFlagOn(config?.remoteKillSwitchFlag);
  }

  /**
   * "Plan it with me": the conversation in place of "Say it instead".
   * Rides on voice (same kill switch), plus its own build-time switch.
   */
  voiceChatEnabled(): boolean {
    return this.voiceEnabled() && !!environment.voiceCreate?.chat;
  }

  /**
   * The server speech-to-text endpoint for browsers that can't transcribe on
   * their own. '' = no fallback (those browsers get typing only).
   */
  get voiceTranscribeUrl(): string {
    return environment.voiceCreate?.transcribeUrl || '';
  }

  /** Hard stop on one recording, in seconds. */
  get voiceMaxSeconds(): number {
    return environment.voiceCreate?.maxSeconds ?? 30;
  }
}
